import type { ReactNode, VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import type { Issue, DependencyUsage, IgnoredKindMetadata, JiraTicket } from '../../../../../../shared/issuesV2';
import { IgnoredKind } from '../../../../../../shared/issuesV2';
import BaseIssueItemContent from './BaseIssueItemContent';
import * as IssuesV2Store from '../../../../../redux/issuesV2';
import { fetcherToPlainText, isLinuxFetcher } from '../../../../../../shared/fetchers';
import Locator from '../../../../../../shared/Locator';
import type { LinuxPackageParts } from '../../../../../../shared/linux';
import { parseLinuxPackage } from '../../../../../../shared/linux';
import IssueTitle from '../IssueTitle';
import IgnoredIssueBadge from '../IgnoredIssueBadge';
import IssueTicketBadge from '../IssueTicketBadge';
import WithSeparator from '../WithSeparator';
import { Badge, BadgeColors } from '../../../../generic';
import DepthIndicator from '../DepthIndicator';
import ProjectsList from '../ProjectsList';
import FoundDateBadge from '../FoundDateBadge';
import IgnoredKindBadge from '../IgnoredKindBadge';
import type { ReduxStore } from '../../../../../redux/store';

type Props = {
  issue: Issue;
  locator: string;
  title?: string;
  dependency: string;
  version: string | null;
  tickets?: JiraTicket[];
  badge: ReactNode;
  packageManager: string;
  depths: Record<DependencyUsage, number>;
  createdAt: string;
  ignoredAt?: string;
  ignoredKinds?: IgnoredKindMetadata[];
};

/**
 * This component displays the content of the licensing or quality issue row.
 */
const NonVulnerabilityIssueItemContent: VoidFunctionComponent<Props> = ({
  issue,
  locator,
  title,
  dependency,
  version,
  tickets,
  packageManager,
  depths,
  createdAt,
  ignoredAt,
  ignoredKinds,
  badge,
}) => {
  const grouping = useSelector(IssuesV2Store.selectors.getGrouping);
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issueDetailsURL = useSelector((state: ReduxStore) =>
    IssuesV2Store.selectors.getIssueDetailsURL(state, issue.id));
  const isLinuxDep = isLinuxFetcher(packageManager);

  const rawPackageName = Locator.parse(locator).package || dependency;
  // the output of parseLinuxPackage is guaranteed to have distroVersion because we are
  // checking if the input is a linux dep, which must have name, distro, and version within it
  const osVersion = isLinuxDep ? ` (${(parseLinuxPackage(rawPackageName) as LinuxPackageParts).distroVersion})` : '';

  // The projects to use for the projects list.  The empty list disables it if we aren't in the global scope.
  const projects = scope.type === 'global' ? issue.projects : [];

  // Whether we are rendering in a grouped view or not for choosing what to render.
  const isUngrouped = grouping === 'issue';

  return (
    <BaseIssueItemContent
      headerSlot={
        <>
          {badge}
          <IssueTitle
            title={title}
            packageManager={packageManager}
            dependency={dependency}
            version={version}
            issueDetailsURL={issueDetailsURL}
          />
        </>
      }
      contentSlot={
        <>
          {ignoredAt && <IgnoredIssueBadge ignoredAt={ignoredAt} />}
          {tickets && (tickets?.length ?? 0) > 0 && <IssueTicketBadge tickets={tickets} />}
          {packageManager && isUngrouped && (
            <WithSeparator>
              <Badge color={BadgeColors.White}>
                {fetcherToPlainText(packageManager) || packageManager}
                {osVersion}
              </Badge>
            </WithSeparator>
          )}
          {depths && isUngrouped && <DepthIndicator depths={depths} />}
          {projects && isUngrouped && projects.length > 0 && (
            <WithSeparator>
              {' '}
              <ProjectsList projects={projects} />{' '}
            </WithSeparator>
          )}
          {createdAt && <FoundDateBadge createdAt={createdAt} />}
        </>
      }
      contentRightSlot={
        ignoredKinds &&
        !!ignoredKinds?.length &&
        ignoredKinds.some((ignore) => ignore.ignoreType === IgnoredKind.IssueException) && (
          <IgnoredKindBadge ignoredKinds={ignoredKinds} numProjects={projects.length} />
        )
      }
    />
  );
};

export default NonVulnerabilityIssueItemContent;
